import { type FirebaseApp, getApps, initializeApp } from 'firebase/app';
import {
  type Auth,
  browserLocalPersistence,
  browserSessionPersistence,
  getAuth,
  indexedDBLocalPersistence,
  initializeAuth,
} from 'firebase/auth';
import { type Firestore, connectFirestoreEmulator, getFirestore } from 'firebase/firestore';
import { type FirebaseStorage, connectStorageEmulator, getStorage } from 'firebase/storage';
import type { TakiConfig } from '@taki/runtime-config';

/**
 * Instances Firestore déjà branchées sur l'émulateur.
 *
 * `connectFirestoreEmulator` ne tolère pas d'être appelé deux fois sur la même
 * instance : comme `initFirebase` RÉUTILISE une app existante (donc la même
 * instance Firestore), on mémorise ce qui est déjà branché.
 */
const emulatorBoundStores = new WeakSet<Firestore>();
const emulatorBoundStorage = new WeakSet<FirebaseStorage>();

/**
 * Ouvre Firebase Auth pour cette app, SANS résolveur de fenêtre Google.
 *
 * `getAuth(app)` pose `browserPopupRedirectResolver`, et sous Safari (comme
 * sur iOS et les navigateurs mobiles) ce résolveur s'initialise d'avance :
 * Auth attend alors `apis.google.com/js/api.js` puis `gapi.iframes` AVANT de
 * rendre l'utilisateur. Tout l'écran attend deux requêtes vers Google, et
 * reste blanc si elles traînent. Ici, mêmes persistances que `getAuth`, sans
 * le résolveur : la fenêtre Google le reçoit à l'appel (`signInWithGoogle`,
 * `@taki/auth`), et l'écran de connexion la préchauffe.
 *
 * Auth déjà ouverte sur cette app (app réutilisée, ou `getAuth` passé avant) :
 * on rend l'instance existante, sans lever.
 */
export function ouvrirAuth(app: FirebaseApp): Auth {
  try {
    return initializeAuth(app, {
      persistence: [indexedDBLocalPersistence, browserLocalPersistence, browserSessionPersistence],
    });
  } catch (err) {
    if ((err as { code?: unknown }).code !== 'auth/already-initialized') throw err;
    return getAuth(app);
  }
}

/**
 * Initialise (ou réutilise) une application Firebase à partir de la config
 * chargée au runtime. On nomme l'app d'après l'orgId pour permettre plusieurs
 * instances isolées dans un même process sans collision.
 *
 * Si la config runtime porte un bloc `emulators` (dev 100% local, cf.
 * `pnpm dev:local`), Firestore est branché sur l'émulateur au lieu du cloud.
 * C'est bien la CONFIG qui pilote, jamais une variable d'env figée au build.
 */
export function initFirebase(cfg: TakiConfig): { app: FirebaseApp; db: Firestore; storage: FirebaseStorage } {
  const appName = `taki-${cfg.orgId}`;
  const existing = getApps().find((a) => a.name === appName);
  const app = existing ?? initializeApp(cfg.firebaseConfig, appName);
  // Le plus tôt possible : tout `getAuth(app)` ultérieur rendra cette instance.
  // Sans clé API, Auth lève : les apps des tests de rules n'en ont pas.
  if (cfg.firebaseConfig.apiKey) ouvrirAuth(app);
  const db = getFirestore(app);
  const storage = getStorage(app);
  storage.maxUploadRetryTime = 15000;
  storage.maxOperationRetryTime = 15000;

  const firestoreEmulator = cfg.emulators?.firestore;
  if (firestoreEmulator && !emulatorBoundStores.has(db)) {
    connectFirestoreEmulator(db, firestoreEmulator.host, firestoreEmulator.port);
    emulatorBoundStores.add(db);
    console.info(
      `[@taki/sdk] Firestore branché sur l'émulateur ${firestoreEmulator.host}:${firestoreEmulator.port} (données locales jetables).`,
    );
  }

  const storageEmulator = cfg.emulators?.storage;
  if (storageEmulator && !emulatorBoundStorage.has(storage)) {
    connectStorageEmulator(storage, storageEmulator.host, storageEmulator.port);
    emulatorBoundStorage.add(storage);
  }

  return { app, db, storage };
}
